import { useCallback } from 'react';
import { useToast, type UseToastOptions } from '@chakra-ui/react';

/**
 * One return toast at a time on the paths that use it (D51f Phase 5): the
 * Subscription screen's `?success` / `?canceled` effect and the native
 * `subscription_success` deep link (`useDeepLinkHandler`) share this id, so a
 * member who reaches the screen through more than one of them still sees
 * exactly one toast. The native `subscription_canceled` deep link does NOT use
 * it: it shows a plain `toast()` with no id, so it can appear beside one.
 */
export const BILLING_RETURN_TOAST_ID = 'subscription-return';

/**
 * The copy of the two billing-return toasts, in ONE place so the Subscription
 * screen (`?success` / `?canceled`) and the native deep links say the same
 * thing. Only the success toast is shared across the screen and the native
 * deep link (via `BILLING_RETURN_TOAST_ID`), so when a member reaches both
 * the later `toast.update` overwrites the earlier text: two different
 * wordings would visibly morph in place. (The native canceled deep link has
 * no id, so its copy is never updated in place.)
 */
export const BILLING_SUCCESS_TOAST = {
  title: 'Welcome to Paire+!',
  description: 'Your subscription is now active.',
} as const;
export const BILLING_CANCELED_TOAST = {
  title: 'Checkout canceled',
  description: 'No charges were made.',
} as const;

/**
 * Returns `showOnce(options)`: shows the toast under `BILLING_RETURN_TOAST_ID`,
 * or, when one is still on screen, UPDATES it in place instead of stacking a
 * second beside it. (Both paths use the copy above, so the update is
 * invisible; were they to differ, the later one's copy would win.)
 */
export function useBillingReturnToast(): (options: UseToastOptions) => void {
  const toast = useToast();
  return useCallback(
    (options: UseToastOptions) => {
      if (toast.isActive(BILLING_RETURN_TOAST_ID)) toast.update(BILLING_RETURN_TOAST_ID, options);
      else toast({ id: BILLING_RETURN_TOAST_ID, ...options });
    },
    [toast]
  );
}
